/**
 * BindingRegistry - Manages all keybinding registrations and lookups
 * 
 * Features:
 * - Efficient prefix matching for multi-key sequences
 * - Conditional activation support
 * - Conflict detection and resolution
 */

class BindingRegistry {
  constructor() {
    this.bindings = new Map(); // Map<string, Binding>
    this.prefixMap = new Set(); // Set of all prefixes for fast partial matching
    this.listeners = new Set(); // For registry change notifications
  }

  /**
   * Register a new keybinding
   * @param {string} sequence - Key sequence like "g s" or "Escape"
   * @param {string} action - Action identifier
   * @param {Object} options - Additional options
   */
  register(sequence, action, options = {}) {
    if (!sequence || !action) {
      throw new Error('Sequence and action are required');
    }

    const binding = {
      sequence: sequence.trim(),
      action,
      description: options.description || '',
      scope: options.scope || 'global',
      preventDefault: options.preventDefault !== false,
      when: options.when || (() => true), // Conditional activation function
      id: options.id || `${sequence}-${action}`,
      registeredAt: Date.now(),
    };

    // Check for conflicts
    if (this.bindings.has(sequence)) {
      const existing = this.bindings.get(sequence);
      console.warn(`Keybinding conflict: "${sequence}" already bound to "${existing.action}", overriding with "${action}"`);
    }

    this.bindings.set(sequence, binding);
    this.updatePrefixMap();
    this.notifyListeners('register', binding);

    return binding.id;
  }

  /**
   * Unregister a keybinding
   * @param {string} sequence - Key sequence to remove
   */
  unregister(sequence) {
    const binding = this.bindings.get(sequence);
    if (binding) {
      this.bindings.delete(sequence);
      this.updatePrefixMap();
      this.notifyListeners('unregister', binding);
      return true;
    }
    return false;
  }

  /**
   * Find exact binding match
   * @param {string} sequence - Complete key sequence
   * @returns {Object|null} - Binding object if found and active
   */
  findExactMatch(sequence) {
    const binding = this.bindings.get(sequence);
    if (binding && binding.when()) {
      return binding;
    }
    return null;
  }

  /**
   * Check if sequence could be start of a longer binding
   * @param {string} sequence - Partial key sequence
   * @returns {boolean} - True if there are longer sequences starting with this
   */
  hasPartialMatch(sequence) {
    return this.prefixMap.has(sequence);
  }

  /**
   * Get all possible completions for a partial sequence
   * @param {string} partialSequence - Partial key sequence
   * @returns {Array} - Array of possible completions
   */
  getCompletions(partialSequence) {
    const completions = [];
    const parts = partialSequence.split(' ');
    
    for (const [sequence, binding] of this.bindings) {
      const sequenceParts = sequence.split(' ');
      if (sequenceParts.length > parts.length) {
        // Check if this sequence starts with our partial
        let matches = true;
        for (let i = 0; i < parts.length; i++) {
          if (parts[i] !== sequenceParts[i]) {
            matches = false;
            break;
          }
        }
        if (matches && binding.when()) {
          completions.push({
            sequence,
            nextKey: sequenceParts[parts.length],
            binding
          });
        }
      }
    }

    return completions;
  }

  /**
   * Update prefix map for efficient partial matching
   */
  updatePrefixMap() {
    this.prefixMap.clear();
    
    for (const [sequence] of this.bindings) {
      const parts = sequence.split(' ');
      if (parts.length > 1) {
        // Add all prefixes of multi-key sequences
        for (let i = 1; i < parts.length; i++) {
          const prefix = parts.slice(0, i).join(' ');
          this.prefixMap.add(prefix);
        }
      }
    }
  }

  /**
   * Get all registered bindings
   * @param {Object} filters - Optional filters
   * @returns {Array} - Array of bindings
   */
  getAllBindings(filters = {}) {
    const bindings = Array.from(this.bindings.values());
    
    if (filters.scope) {
      return bindings.filter(b => b.scope === filters.scope);
    }
    
    if (filters.active !== undefined) {
      return bindings.filter(b => filters.active ? b.when() : !b.when());
    }

    return bindings;
  }

  /**
   * Clear all bindings
   */
  clear() {
    this.bindings.clear();
    this.prefixMap.clear();
    this.notifyListeners('clear');
  }

  /**
   * Get registry statistics
   */
  getStats() {
    const bindings = Array.from(this.bindings.values());
    const active = bindings.filter(b => b.when()).length;
    const multiKey = bindings.filter(b => b.sequence.includes(' ')).length;
    
    return {
      total: bindings.length,
      active,
      inactive: bindings.length - active,
      multiKey,
      singleKey: bindings.length - multiKey,
      prefixes: this.prefixMap.size,
    };
  }

  /**
   * Add listener for registry changes
   * @param {Function} listener - Callback function
   */
  addListener(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of registry changes
   */
  notifyListeners(type, binding) {
    for (const listener of this.listeners) {
      try {
        listener(type, binding);
      } catch (error) {
        console.error('Keybinding registry listener error:', error);
      }
    }
  }

  /**
   * Export all bindings for debugging/inspection
   */
  export() {
    return {
      bindings: Object.fromEntries(this.bindings),
      prefixes: Array.from(this.prefixMap),
      stats: this.getStats(),
      timestamp: Date.now(),
    };
  }
}

// Create singleton instance
const bindingRegistry = new BindingRegistry();

export default bindingRegistry;